/** Equity curve (SVG): strategy vs hold-BTC, log or linear, hover readout. */
import { useMemo, useState } from "react";
import { dateUtc, usd0 } from "../lib/format.ts";

export function EquityChart({ t, series, log = true, height = 260 }: { t: number[]; series: { label: string; v: number[]; tone: "accent" | "muted" | "warn" }[]; log?: boolean; height?: number }) {
  const W = 1000, H = height, pad = { l: 64, r: 12, t: 10, b: 24 };
  const [hi, setHi] = useState<number | null>(null);
  const geo = useMemo(() => {
    const all = series.flatMap((s) => s.v).filter((x) => x > 0 && Number.isFinite(x));
    if (!all.length || t.length < 2) return null;
    const lo = Math.min(...all) * 0.95, hi = Math.max(...all) * 1.05;
    const f = log ? Math.log : (x: number) => x;
    const y = (v: number) => pad.t + (1 - (f(Math.max(v, lo)) - f(lo)) / (f(hi) - f(lo))) * (H - pad.t - pad.b);
    const x = (i: number) => pad.l + ((t[i]! - t[0]!) / (t[t.length - 1]! - t[0]!)) * (W - pad.l - pad.r);
    const ticks: number[] = [];
    if (log) { for (let p = Math.pow(10, Math.floor(Math.log10(lo))); p < hi; p *= 10) for (const m of [1, 2, 5]) if (p * m >= lo && p * m <= hi) ticks.push(p * m); }
    else for (let k = 0; k <= 4; k++) ticks.push(lo + ((hi - lo) * k) / 4);
    return { y, x, ticks };
  }, [t, series, log, H]);
  if (!geo) return <div className="py-10 text-center text-muted">Not enough data yet.</div>;
  const color = { accent: "rgb(var(--accent))", muted: "rgb(var(--faint))", warn: "rgb(var(--warn))" };
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full" role="img" aria-label={`Equity chart: ${series.map((s) => `${s.label} ${usd0(s.v[s.v.length - 1])}`).join(", ")}`}
        onMouseMove={(e) => { const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect(); const px = ((e.clientX - r.left) / r.width) * W; const tt = t[0]! + ((px - pad.l) / (W - pad.l - pad.r)) * (t[t.length - 1]! - t[0]!); let k = 0; while (k < t.length - 1 && t[k + 1]! <= tt) k++; setHi(k); }}
        onMouseLeave={() => setHi(null)}>
        {geo.ticks.map((v) => <g key={v}><line x1={pad.l} x2={W - pad.r} y1={geo.y(v)} y2={geo.y(v)} stroke="rgb(var(--line))" /><text x={pad.l - 8} y={geo.y(v) + 4} textAnchor="end" fontSize="12" fill="rgb(var(--muted))">{usd0(v)}</text></g>)}
        {series.map((s) => {
          let pen = "M"; // gaps (NaN) lift the pen
          const d = s.v.map((v, i) => { if (!Number.isFinite(v) || v <= 0) { pen = "M"; return ""; } const seg = `${pen}${geo.x(i).toFixed(1)} ${geo.y(v).toFixed(1)}`; pen = "L"; return seg; }).join(" ");
          return <path key={s.label} d={d} fill="none" stroke={color[s.tone]} strokeWidth={s.tone === "accent" ? 2.2 : 1.5} />;
        })}
        {hi !== null && <line x1={geo.x(hi)} x2={geo.x(hi)} y1={pad.t} y2={H - pad.b} stroke="rgb(var(--line-strong))" />}
        <text x={pad.l} y={H - 6} fontSize="12" fill="rgb(var(--muted))">{dateUtc(t[0]!)}</text>
        <text x={W - pad.r} y={H - 6} textAnchor="end" fontSize="12" fill="rgb(var(--muted))">{dateUtc(t[t.length - 1]!)}</text>
      </svg>
      <div className="mt-1 flex flex-wrap gap-4 text-[12px] text-muted">
        {hi !== null && <span className="num">{dateUtc(t[hi]!)}</span>}
        {series.map((s) => <span key={s.label} className="flex items-center gap-1.5"><span className="inline-block h-0.5 w-4" style={{ background: color[s.tone] }} />{s.label}{hi !== null && Number.isFinite(s.v[hi]) && <span className="num text-ink">{usd0(s.v[hi])}</span>}</span>)}
      </div>
    </div>
  );
}
